import React from 'react';
import {
  Modal,
  FormGroup,
  Row,
  FormControl,
  Col,
  ControlLabel,
  Button,
  ButtonToolbar,
  Checkbox,
} from 'react-bootstrap';

import { connect } from 'react-redux';
import { compose } from 'recompose';

import { Field, reduxForm, formPropTypes, SubmissionError, formValueSelector } from 'redux-form';
import PropTypes from 'prop-types';

import DepartmentInput from '../../../fields/DepartmentInput';
import DistrictInput from '../../../fields/DistrictInput';
import { maybeId } from '../../../../utils';
import { BaseField } from '../../../fields/index';
import EquipmentTypeListInput from '../../../fields/EquipmentTypeListInput/index';

export const FORM_NAME = 'equipmentForm';

/* eslint-disable */
const proptypes = {
  showEquipmentModal: PropTypes.bool.isRequired,
  closeEquipmentModal: PropTypes.func.isRequired,

  districtId: PropTypes.number,
  departmentId: PropTypes.number,
  initialValues: PropTypes.object,

  dispatchCreateUpdateEquipment: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  ...formPropTypes,
};
/* eslint-enable */

const validate = (values) => {
  const errors = {};

  if (!values.equipment_number) {
    errors.equipment_number = 'Required';
  }
  if (!values.equipment_type) {
    errors.equipment_type = 'Required';
  }
  if (!values.department) {
    errors.department = 'Required';
  }
  if (!values.district) {
    errors.district = 'Required';
  }

  return errors;
};

/* eslint-disable */
const createRenderer = (render) => ({ input, meta, label, ...rest }) => {
  return (
    <div
      className={[
        meta.error && meta.touched ? 'form-error' : '',
        meta.active ? 'form-active' : '',
      ].join(' ')}
    >
      <ControlLabel>
        {label}
      </ControlLabel>

      {render(input, label, rest)}

      {meta.error && meta.touched &&
      <span>
          {meta.error}
        </span>
      }
    </div>
  );
};
/* eslint-enable */

const RenderInput = createRenderer((input, label) => {
  return (
    <FormControl {...input} />
  );
});

const RenderDepartment = createRenderer((input, label, { disabled, districtId }) => {
  return (
    <DepartmentInput districtId={districtId} disabled={disabled} {...input} />
  );
});

const RenderDistrict = createRenderer((input, label, { disabled, departmentId }) => {
  return (
    <DistrictInput departmentId={departmentId} disabled={disabled} {...input} />
  );
});

const RenderEquipmentType = createRenderer((input, label, { disabled, equipmentType }) => {
  return (
    <EquipmentTypeListInput value={equipmentType} disabled={disabled} {...input} />
  );
});

const RenderCheckBox = createRenderer((input, label, { disabled }) => {
  return (
    <Checkbox checked={input.value} disabled={disabled} {...input} />
  );
});


const EquipmentModal = (props) => {
  const {
    handleSubmit,
    dispatchCreateUpdateEquipment,
    initialValues,
    closeEquipmentModal,
    showEquipmentModal,
    submitting,
    pristine,
    invalid,
    error,
    districtId,
    departmentId,
  } = props;
  const isNewRecord = !(initialValues.id);

  const onSubmit = async (values) => {
    const {
      equipment_number,
      equipment_type,
      department,
      district,
      enabled,
      notes,
    } = values;

    const equipment = {
      equipment_number: parseInt(equipment_number, 10),
      equipment_type,
      department_id: department.id,
      district_id: district.id,
      enabled,
      notes,
    };

    if (values.id) {
      equipment.id = values.id;
    }

    try {
      await dispatchCreateUpdateEquipment(equipment);
    } catch (e) {
      const message = e.graphQLErrors.reduce((errorMessage, gqlError) => {
        return errorMessage + gqlError.details;
      }, '');
      throw new SubmissionError({ _error: message });
    }
  };

  return (
    <React.Fragment>
      <Modal
        show={showEquipmentModal}
        onHide={closeEquipmentModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Equipment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormGroup>
              <Row>
                <Col xs={6}>
                  <FormGroup>
                    <Field
                      name="equipment_number"
                      type="text"
                      label="Equipment Number"
                      component={RenderInput}
                    />
                  </FormGroup>
                </Col>
                <Col xs={6}>
                  <FormGroup>
                    <Field
                      name="equipment_type"
                      type="select"
                      label="Equipment Type"
                      component={RenderEquipmentType}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col xs={6}>
                  <FormGroup>
                    <Field
                      districtId={districtId}
                      name="department"
                      type="select"
                      label="Department"
                      format={null}
                      component={RenderDepartment}
                    />
                  </FormGroup>
                </Col>
                <Col xs={6}>
                  <FormGroup>
                    <Field
                      departmentId={departmentId}
                      name="district"
                      type="select"
                      label="District"
                      format={null}
                      component={RenderDistrict}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <FormGroup>
                    <Field
                      name="enabled"
                      type="checkbox"
                      label="Enabled"
                      format={null}
                      component={RenderCheckBox}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <FormGroup>
                    <BaseField
                      name="notes"
                      componentClass="textarea"
                      label="Notes"
                    />
                  </FormGroup>
                </Col>
              </Row>
            </FormGroup>
            <Row>
              <Col md={12}>
                <ButtonToolbar className="pull-right">
                  <Button bsStyle="warning" onClick={closeEquipmentModal}>
                    Cancel
                  </Button>
                  <Button
                    bsStyle="primary"
                    type="submit"
                    disabled={submitting || pristine || invalid}
                  >
                    {isNewRecord && 'Create'}
                    {!isNewRecord && 'Update'}
                  </Button>
                </ButtonToolbar>
              </Col>
            </Row>
            <label>{error && <strong>{error}</strong>}</label>
          </form>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

EquipmentModal.propTypes = proptypes;
EquipmentModal.defaultProps = {
  initialValues: {},
};

const withForm = reduxForm({
  form: FORM_NAME,
  enableReinitialize: true,
  validate,
});

const selector = formValueSelector(FORM_NAME);

const withRedux = connect((state) => {
  const district = selector(state, 'district');
  const department = selector(state, 'department');
  const districtId = maybeId(district);
  const departmentId = maybeId(department);

  return {
    districtId,
    departmentId,
  };
});

const enhance = compose(
  withForm,
  withRedux,
);

export default enhance(EquipmentModal);
